import moment from "moment";
import { debounce, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@material-ui/core";
import { LoadingComponent } from "../loading/LoadingComponent";
import { useSnackbar } from "notistack";
import * as Styles from "./styles/MyAccountContainerStyles";
import React from "react";
import { GetBidderLoginHistory } from "../../services/BidderService";
import { IPagingResults } from "../../interfaces/paging/paging";
import { IBidderLoginHistory } from "../../interfaces/bidders/IBidderLoginHistory";
import useDebounce from "../../hooks/useDebounce";

export const LoginHistoryTable: React.FC = () => {
  const classes = Styles.MyAccountContainerStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [pageNumber, setPageNumber] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [search, setSearch] = React.useState<string>('');
  const [fromDate, setFromDate] = React.useState<Date | null>(null);
  const [toDate, setToDate] = React.useState<Date | null>(null);
  const [bidderLoginHistory, setBidderLoginHistory] = React.useState<IPagingResults<IBidderLoginHistory>>();
  const [loadingBidderLoginHistory, setLoadingBidderLoginHistory] = React.useState(false);

  // const fromDateDebounce = useDebounce(fromDate, 800);
  // const toDateDebounce = useDebounce(toDate, 800);

  React.useEffect(() => {
    refreshLoginHistoryData();
  }, [pageNumber, pageSize]);

  React.useEffect(() => {
    setPageNumber(0);
    refreshLoginHistoryData();
  }, [fromDate, toDate]);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPageNumber(newPage);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (pageNumber > 0) {
        setPageNumber(0); // If not on first page, this will trigger reload of data
      } else {
        refreshLoginHistoryData(); // if on first page, page number update wont trigger reload so trigger manually.
      }
    }
  };

  const refreshLoginHistoryData = () => {
    setLoadingBidderLoginHistory(true);

    GetBidderLoginHistory(pageNumber, pageSize, {
      search: search,
      fromDate: fromDate,
      toDate: toDate
    }).then(result => {
      setBidderLoginHistory(result.parsedBody as IPagingResults<IBidderLoginHistory>);
    }).catch(() => {
      enqueueSnackbar("There was a problem loading your login history, please try again.", { variant: "error" })
    }).finally(() => {
      setLoadingBidderLoginHistory(false);
    });
  }

  const paginationComponent = (
    <TablePagination
      component="div"
      count={bidderLoginHistory?.count ? bidderLoginHistory?.count : 0}
      page={pageNumber}
      rowsPerPage={pageSize}
      rowsPerPageOptions={[
        { value: 10, label: "10" },
        { value: 20, label: "20" },
        { value: 30, label: "30" },
      ]}
      labelRowsPerPage="Rows"
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      nextIconButtonProps={{ style: { padding: 0 } }}
      backIconButtonProps={{ style: { padding: 0 } }}
    />
  );

  return (
    <Grid container>
      {loadingBidderLoginHistory && (
        <Grid item xs={12}>
          <LoadingComponent label="Loading your login history" marginTop="50px" />
        </Grid>
      )}

      {!loadingBidderLoginHistory && (
        <>
          <Grid item container xs={12} justifyContent="flex-end" spacing={2} style={{ marginBottom: "1rem" }}>
            <Grid item>
              <TextField
                variant="outlined"
                disabled={loadingBidderLoginHistory}
                placeholder="From Date"
                type="date"
                label="From"
                margin="dense"
                value={fromDate ? fromDate.toISOString().split("T")[0] : ""}
                onChange={(event) => {
                  const selectedDate = event.target.value ? new Date(event.target.value) : null;
                  if (selectedDate && toDate && selectedDate > toDate) {
                    setToDate(selectedDate); // Adjust toDate to prevent overlap
                  }
                  setFromDate(selectedDate);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: toDate ? toDate.toISOString().split("T")[0] : undefined, // Prevent fromDate > toDate
                }}
                onKeyDown={(event) => event.preventDefault()} // Prevent manual typing
              />
            </Grid>

            <Grid item>
              <TextField
                variant="outlined"
                disabled={loadingBidderLoginHistory}
                placeholder="To Date"
                type="date"
                label="To"
                margin="dense"
                value={toDate ? toDate.toISOString().split("T")[0] : ""}
                onChange={(event) => {
                  const selectedDate = event.target.value ? new Date(event.target.value) : null;
                  if (selectedDate && fromDate && selectedDate < fromDate) {
                    setFromDate(selectedDate); // Adjust fromDate to prevent overlap
                  }
                  setToDate(selectedDate);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: fromDate ? fromDate.toISOString().split("T")[0] : undefined, // Prevent toDate < fromDate
                }}
                onKeyDown={(event) => event.preventDefault()} // Prevent manual typing
              />
            </Grid>

            <Grid item>
              <TextField
                variant="outlined"
                disabled={loadingBidderLoginHistory}
                placeholder="Search IP Address"
                type="text"
                margin="dense"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                onKeyPress={handleKeyPress}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} lg={12}>
            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Login Date</TableCell>
                    <TableCell>IP Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bidderLoginHistory?.count === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="subtitle1" component="h2">
                          There is currently no login history available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {bidderLoginHistory?.results.map((loginHistory, index) => (
                    <TableRow className={classes.pointer} key={index}>
                      <TableCell>{moment(loginHistory.loginDate).format('DD/MM/YYYY HH:mma')}</TableCell>
                      <TableCell>{loginHistory.ipAddress}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} lg={12}>
            {paginationComponent}
          </Grid>
        </>
      )}
    </Grid>
  );
};
